import { useState, useMemo } from "react";
import {
  FILTER_IDS_BLACKLIST,
  LIFECYCLE_FILTER_OPTIONS,
  PERCENT_COLUMNS_NOT_IN_DECIMAL_FORMAT,
} from "../modules/merchandising/components/tables/filters";
import { getTargetBidTypeFilter } from "../modules/advertising/filters/targetLifecycleFilters";
import useTableSettings, { type CustomFilter } from "./useTableSettings";
import { compareFilterValue } from "../components/table/filterFns";

const useCustomTableFilters = (tableId: string, initialFilters: CustomFilter[] = []) => {
  // `initialFilters` apply until the user saves their own (an explicit empty list included).
  const { savedTableSettings: savedTableFilters, updateTableSettings: setSavedTableFilters } = useTableSettings(
    tableId,
    "tableFilters",
    initialFilters,
  );

  const filterSettings = useMemo(() => {
    return (savedTableFilters ?? [])
      .filter((filterOption) => {
        return !FILTER_IDS_BLACKLIST.includes(filterOption.id);
      })
      .map((filterOption) => {
        if (filterOption.override) {
          const matchedLifecycleOption = [...LIFECYCLE_FILTER_OPTIONS, getTargetBidTypeFilter(tableId)].find(
            (option) => option.id === filterOption.id,
          )!;
          filterOption.filterFunction = matchedLifecycleOption.filterFunction;
        }
        return filterOption;
      });
  }, [tableId, savedTableFilters]);

  const [customFilters, setCustomFilters] = useState(filterSettings);

  const applyCustomFilters = (data?: Record<string, unknown>[]) => {
    // Purge incomplete filter options without needed values to match or missing operator
    const applicableFilters = customFilters.filter((filter) => {
      const isNumericFilter = filter.format && ["currency", "percent", "number"].includes(filter.format);
      return (
        filter.id &&
        filter.operator &&
        (filter.value ||
          (filter.min && filter.max) ||
          filter.format === "toggle" ||
          filter.override ||
          (isNumericFilter && !isNaN(parseFloat(filter.value as string))))
      );
    });

    if (!applicableFilters.length) {
      return data;
    }
    return data?.filter((row) => {
      for (let filterIndex = 0; filterIndex < applicableFilters.length; filterIndex++) {
        const {
          id,
          value,
          operator,
          format,
          min,
          max,
          onValue,
          offValue,
          override,
          filterFunction,
          overrideAccessorId,
        } = applicableFilters[filterIndex]!;

        const rowAccessor = overrideAccessorId ?? id;

        const sharedFiltersByColumnId = applicableFilters.filter((f) => f.id === applicableFilters[filterIndex]?.id);

        // Handle overrides - use custom filter function to return any rows that match criteria
        if (override) {
          // Multi-select values and support for multiple column filter criteria (not currently supported in markup)
          if (sharedFiltersByColumnId.length > 1 || typeof value === "object") {
            const valuesList = sharedFiltersByColumnId.map((f) => f.value);

            let match = false;
            const matchValues = valuesList.flat();

            if (operator === "allof") {
              match = matchValues.every((value) => filterFunction?.(row, value));
            } else {
              for (let matchValueIndex = 0; matchValueIndex < matchValues.length; matchValueIndex++) {
                if (filterFunction?.(row, matchValues[matchValueIndex])) {
                  match = true;
                }
              }
            }
            if (!match) {
              return false;
            }
          } else if (!filterFunction?.(row, value)) {
            return false;
          }
        } else if (PERCENT_COLUMNS_NOT_IN_DECIMAL_FORMAT.includes(id)) {
          const convertedRowValue =
            !isNaN(parseFloat(row[rowAccessor as string] as string)) ?
              parseFloat(row[rowAccessor as string] as string) / 100
            : null;
          if (
            !compareFilterValue(convertedRowValue, value, operator, format, {
              min,
              max,
              onValue,
              offValue,
            })
          ) {
            return false;
          }
        } else if (
          !compareFilterValue(row[rowAccessor], value, operator, format, {
            min,
            max,
            onValue,
            offValue,
          })
        ) {
          return false;
        }
      }
      return true;
    });
  };

  const updateCustomFilters = (updatedFilters: CustomFilter[]) => {
    setCustomFilters(updatedFilters);

    const updatedTableFilters = updatedFilters.filter(
      (filterOption: { id: string }) => !FILTER_IDS_BLACKLIST.includes(filterOption.id),
    );

    setSavedTableFilters(updatedTableFilters);
  };

  return {
    customFilters,
    setCustomFilters: updateCustomFilters,
    applyCustomFilters,
  };
};

export default useCustomTableFilters;
